import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildGoodDeliveryQuery } from './goodDeliveryConfig';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footerr from 'components/footers/Footer';
import LightAdmin from 'components/headers/lightAdmin';

const BACKEND_COLIS_URL = 'https://jolyaparis.cloud/api/gooddelivery/colis';
const GOOD_DELIVERY_COLIS_URL = 'https://gooddelivery.ma/colislist.php';
const GOOD_DELIVERY_PORTAL_URL = 'https://gooddelivery.ma/is-admin/ramassage.php';

const coerceText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) {
    return value
      .map((entry) => coerceText(entry))
      .filter(Boolean)
      .join(' / ')
      .trim();
  }
  if (typeof value === 'object') {
    return coerceText(Object.values(value));
  }
  return '';
};

const coerceNumber = (value) => {
  const numeric = typeof value === 'string' ? Number(value.replace(/[^0-9.-]+/g, '')) : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeShipment = (item, index) => {
  if (!item || typeof item !== 'object') {
    return {
      id: `unknown-${index}`,
      code: '',
      internalId: '',
      createdAt: '',
      recipient: '',
      phone: '',
      address: '',
      city: '',
      price: null,
      status: coerceText(item),
      note: '',
      product: '',
      quantity: '',
      raw: item,
    };
  }

  const createdAt =
    item.date_ajout ??
    item.dateAjout ??
    item.created_at ??
    item.createdAt ??
    item.date ??
    item.Date ??
    '';

  const recipient =
    item.destinataire ??
    item.recipient ??
    item.client ??
    item.customer ??
    item.customer_name ??
    '';

  const phone = item.telephone ?? item.phone ?? item.tel ?? item.Phone ?? '';
  const address = item.adresse ?? item.address ?? item.Address ?? '';
  const city = item.ville ?? item.city ?? item.City ?? '';
  const price = item.prix ?? item.price ?? item.Price ?? item.amount ?? item.Amount ?? null;
  const status =
    item.State ??
    item.status ??
    item.state ??
    item.Status ??
    item.current_status ??
    '';
  const note = item.note ?? item.commentaire ?? item.comment ?? item.Comment ?? '';
  const product = item.produit ?? item.product ?? item.products ?? item.description ?? '';
  const quantity = item.qty ?? item.quantity ?? item.Quantite ?? item.Quantity ?? '';

  const code =
    item.id_colis ??
    item.idColis ??
    item.IDColis ??
    item.code_colis ??
    item.code ??
    item.Code ??
    item.tracking_code ??
    '';
  const internalId = item.id_intern ?? item.idInterne ?? item.IDInterne ?? item.internal_id ?? '';

  return {
    id: coerceText(item.id ?? item.ID ?? code ?? internalId) || `entry-${index}`,
    code: coerceText(code),
    internalId: coerceText(internalId),
    createdAt: coerceText(createdAt),
    recipient: coerceText(recipient),
    phone: coerceText(phone),
    address: coerceText(address),
    city: coerceText(city),
    price: coerceNumber(price),
    priceLabel: coerceText(price),
    status: coerceText(status),
    note: coerceText(note),
    product: coerceText(product),
    quantity: coerceText(quantity),
    raw: item,
  };
};

const normalizePayload = (payload) => {
  if (!payload) {
    return { items: [], message: '' };
  }

  if (Array.isArray(payload)) {
    return { items: payload.map(normalizeShipment), message: '' };
  }

  const arrays = [
    payload.colis,
    payload.data,
    payload.items,
    payload.result,
    payload.shipments,
    payload.list,
    payload.orders,
  ];

  const shipments = arrays.find((candidate) => Array.isArray(candidate)) ?? [];

  const message = coerceText(
    payload.message ?? payload.msg ?? payload.error ?? payload.status_text ?? payload.remark ?? ''
  );

  return {
    items: shipments.map(normalizeShipment),
    message,
  };
};

const buildColisListUrl = (params = {}) => {
  const query = buildGoodDeliveryQuery(params);
  return `${GOOD_DELIVERY_COLIS_URL}?${query}`;
};

const buildBackendUrl = (params = {}) => {
  const query = new URLSearchParams(params);
  const base = BACKEND_COLIS_URL;
  return query.toString() ? `${base}?${query.toString()}` : base;
};

const Delivery = () => {
  const { t } = useTranslation();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [serverMessage, setServerMessage] = useState('');
  const [fallbackUrl, setFallbackUrl] = useState('');

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    setError('');
    setFallbackUrl('');
    setServerMessage('');

    const url = buildBackendUrl();

    try {
      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response) {
        throw new Error('Aucune réponse du serveur.');
      }

      if (response.type === 'opaque') {
        throw new Error(
          t(
            'delivery.opaque_response',
            'Réponse GoodDelivery inaccessible (CORS). Ouvrez le portail dans un nouvel onglet.'
          )
        );
      }

      const contentType = response.headers.get('content-type') || '';
      let payload;

      if (contentType.includes('application/json')) {
        payload = await response.json();
      } else {
        const text = await response.text();
        try {
          payload = JSON.parse(text);
        } catch (parseError) {
          payload = { raw: text };
        }
      }

      if (!response.ok) {
        const message =
          (payload && typeof payload.message === 'string' && payload.message) ||
          `HTTP ${response.status}`;
        throw new Error(message);
      }

      const { items, message } = normalizePayload(payload);
      setShipments(items);
      setServerMessage(message);
      setLastUpdated(Date.now());
      setFallbackUrl('');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setShipments([]);
      const corsFailure = /Failed to fetch|CORS|Access-Control-Allow-Origin|TypeError: NetworkError/i.test(
        message
      );
      if (corsFailure) {
        setFallbackUrl(buildColisListUrl());
        setError(
          t(
            'delivery.cors_error',
            'Le navigateur bloque l’appel GoodDelivery (CORS). Ouvrez la liste dans un nouvel onglet.'
          )
        );
      } else {
        setError(message || t('delivery.unknown_error', 'Erreur inattendue.'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);
  console.log('shipments',shipments);
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

 const filteredShipments = useMemo(() => {
  if (!searchTerm.trim()) return shipments;

  const lookup = searchTerm.trim().toLowerCase();

  return shipments.filter((shipment) => {
    return [
      shipment.code,
      shipment.internalId,
      shipment.raw?.Fullname,
      shipment.raw?.Phone,   // <-- uppercase P since your JSON shows Phone
      shipment.city,
      shipment.status,
      shipment.note,
      shipment.product,
    ]
      .map((value) =>
        typeof value === "string" ? value.toLowerCase() : String(value || "")
      )
      .some((value) => value.includes(lookup));
  });
}, [shipments, searchTerm]);

  const handleOpenPortal = () => {
    window.open(GOOD_DELIVERY_PORTAL_URL, '_blank', 'noopener,noreferrer');
  };

  return (
      <AnimationRevealPage>
                <LightAdmin />
    <section className="w-100 p-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="h4 mb-1">{t('delivery.title', 'Suivi GoodDelivery')}</h2>
          <p className="text-muted mb-0">
            {t(
              'delivery.subtitle',
              'Liste des colis GoodDelivery récupérée depuis l’API partenaire.'
            )}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleOpenPortal}
          >
            {t('delivery.open_portal', 'Ouvrir le portail')}
          </button>
          <button
            type="button"
              style={{ backgroundColor: '#000000' ,color:'white'}}

            className="btn"
            onClick={fetchShipments}
            disabled={loading}
          >
            {loading ? t('delivery.loading', 'Chargement...') : t('delivery.refresh', 'Actualiser')}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder={t('delivery.search_placeholder', 'Rechercher par code, client, ville…')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <div className="d-flex flex-column gap-2">
            <span>{error}</span>
            {fallbackUrl && (
              <button
                type="button"
                className="btn btn-outline-light align-self-start"
                onClick={() => window.open(fallbackUrl, '_blank', 'noopener,noreferrer')}
              >
                {t('delivery.open_raw_feed', 'Ouvrir la liste GoodDelivery')}
              </button>
            )}
          </div>
        </div>
      )}

      {!error && serverMessage && (
        <div className="alert alert-info" role="alert">
          {serverMessage}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-primary">
            <tr>
              <th>{t('delivery.code', 'ID Colis')}</th>
              <th>{t('delivery.created_at', 'Date ajout')}</th>
              <th>{t('delivery.recipient', 'Destinataire')}</th>
              <th>{t('delivery.phone', 'Téléphone')}</th>
              <th>{t('delivery.address', 'Adresse')}</th>
              <th>{t('delivery.city', 'Ville')}</th>
              <th>{t('delivery.price', 'Prix')}</th>
              <th>{t('delivery.status', 'État')}</th>
              <th>{t('delivery.note', 'Note')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={12} className="text-center text-muted py-4">
                  {t('delivery.loading', 'Chargement...')}
                </td>
              </tr>
            )}
            {!loading && filteredShipments.length === 0 && (
              <tr>
                <td colSpan={12} className="text-center text-muted py-4">
                  {t('delivery.empty', 'Aucun colis disponible.')}
                </td>
              </tr>
            )}
            {!loading &&
              filteredShipments.map((shipment) => (
                <tr key={shipment.id}>
                  <td>{shipment.code || '—'}</td>
<td>
  {shipment.raw?.DateAdd 
    ? new Date(Number(shipment.raw.DateAdd) * 1000).toLocaleString()
    : "—"}
</td>
                  <td>{shipment.raw?.Fullname || '—'}</td>
                  <td>
                    {shipment.phone ? (
                      <a href={`tel:${shipment.phone}`}>
                        {shipment.phone}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{shipment.address || '—'}</td>
                  <td>{shipment.city || '—'}</td>
                  <td>
                    {shipment.price !== null
                      ? `${shipment.price.toFixed(2)} DH`
                      : shipment.priceLabel || '—'}
                  </td>
                  <td>{shipment.status || '—'}</td>
                  <td>{shipment.note || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {lastUpdated && (
        <p className="text-muted small mt-3 mb-0">
          {t('delivery.last_update', { value: new Date(lastUpdated).toLocaleString() })}
        </p>
      )}
    </section>
       <Footerr />
            </AnimationRevealPage>
  );
};

export default Delivery;
